/**
 * AI推荐标题
 */
import {useCallback, useEffect, useRef, useState} from 'react';
import Lottie from 'lottie-react';

import sdk from '@/sdk';
import * as store from '@/store';
import {ServerConfig} from '@/config/routers';
import {isMac} from '@/config/env';
import hotkeys, {hotkeyMap} from '@/hotkeys';
import {
    reportTitleRecommendBtnClick,
    reportTitleRevokeBtnClick,
    reportTitleRecommendBtnExposure,
} from '@/buried/aigc';
import Icon, {IconSize} from '@/components/Icon';
import toast, {ToastType} from '@/components/Toast';
import OutlineSvg from '@/assets/svg/ai/outline.svg?tsx';
import CloseSvg from '@/assets/svg/ai/close.svg?tsx';
import pointLoading from '@/assets/lottieFiles/point-loading.json';

import styles from './index.module.scss';

const revokeHotKey = isMac ? hotkeyMap.COMMAND_Z : hotkeyMap.CTRL_Z;

enum Status {
    Null = 1,
    Init,
    Loading,
    Revocable,
}

interface IProps {
    title: string;
    setTitle: (title: string) => void;
    isFocus: boolean;
    isHover: boolean;
    isDocEmpty?: boolean;
    docGuid: string;
}

const AIRecommendTitle: React.FC<IProps> = ({title, setTitle, isFocus, isHover, isDocEmpty = false, docGuid}) => {
    const [status, setStatus] = useState(Status.Init);
    const prevTitleRef = useRef<string | null>(null);
    const [recommendTitleText, setRecommendTitleText] = useState<string | null>(null);

    // 打字机效果
    function typing(text: string, index: number = 0) {
        const length = text.length;
        if (index <= length) {
            const stepLength = Math.random() < 0.5 ? 1 : 2;
            const nextStr = text.substring(0, index += stepLength);
            setTitle(nextStr);
            setTimeout(typing.bind(null, text, index), 100);
        } else {
            setStatus(Status.Revocable);
        }
    }

    function loadTitle() {
        setStatus(Status.Loading);

        const {accessToken: rteToken} = store.getData(store.KEYS.CUR_DOCUMENT_TOKEN);
        sdk.as.request({
            method: 'get',
            url: `${ServerConfig.origin}/wiki/ai/recommend/v1/titles`,
            headers: {
                Authorization: rteToken,
            },
        }).then(result => {
            const newTitle = result[0];
            if (newTitle) {
                prevTitleRef.current = title;
                setRecommendTitleText(newTitle);
                typing(newTitle);
            } else {
                toast({type: ToastType.Error, content: '暂无推荐标题', duration: 3000});
                setStatus(Status.Null);
            }
        }).catch(err => {
            if (err.code === 30001) {
                toast({type: ToastType.Error, content: '暂无推荐标题', duration: 3000});
            } else {
                toast({type: ToastType.Error, content: '操作失败，请稍候再试', duration: 3000});
            }

            setStatus(Status.Null);
        });

        reportTitleRecommendBtnClick({pageId: docGuid});
    }

    const revoke = useCallback(() => {
        if (prevTitleRef.current !== null) {
            setTitle(prevTitleRef.current);
            prevTitleRef.current = null;
        }

        reportTitleRevokeBtnClick({pageId: docGuid});
    }, [docGuid, setTitle]);

    const onRevoke = () => {
        revoke();
        setTimeout(() => {
            setStatus(Status.Null);
        }, 300);
    };

    // 避免点击按钮时焦点变化，导致触发保存标题
    function onMouseDown(e: React.MouseEvent<HTMLDivElement>) {
        e.stopPropagation();
        e.preventDefault();
    }

    useEffect(() => {
        if (status === Status.Revocable && !isFocus) {
            setTimeout(() => {
                setStatus(Status.Null);
            }, 600);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFocus]);

    // 状态变成null后，一秒钟后再初始化
    useEffect(() => {
        if (status === Status.Null) {
            setTimeout(() => {
                setStatus(Status.Init);
            }, 1000);
        }
    }, [status]);

    // 显示放弃推荐按钮的情况下，用户再修改标题则隐藏放弃推荐按钮
    useEffect(() => {
        if (status === Status.Revocable && recommendTitleText && recommendTitleText !== title) {
            setStatus(Status.Null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [title]);

    // 放弃推荐快捷键
    useEffect(() => {
        hotkeys(revokeHotKey, (e: KeyboardEvent) => {
            if (isFocus && prevTitleRef.current !== null) {
                e.preventDefault();
                revoke();
            }
        });

        return () => {
            hotkeys.unbind(revokeHotKey);
        };
    }, [isFocus, revoke]);

    const show = !isDocEmpty;

    const showRecommendBtn = show && isHover && status === Status.Init;
    useEffect(() => {
        if (showRecommendBtn) {
            reportTitleRecommendBtnExposure({pageId: docGuid});
        }
    }, [docGuid, showRecommendBtn]);

    return show ? <div className={styles.box} onMouseDown={onMouseDown}>
        {isHover && status === Status.Init && <div className={styles.btn} onClick={loadTitle}>
            <Icon className={styles.icon} svg={OutlineSvg} size={IconSize.LG} noSpace />
            <div className={styles.text}>推荐标题</div>
        </div>}
        {status === Status.Loading && <div className={styles.loading}>
            <div>标题推荐中</div>
            <Lottie className={styles.point} animationData={pointLoading} loop />
        </div>}
        {status === Status.Revocable && <div className={styles.btn} onClick={onRevoke}>
            <Icon className={styles.icon} svg={CloseSvg} size={IconSize.LG} noSpace />
            <div className={styles.text}>放弃推荐</div>
        </div>}
    </div> : null;
};

export default store.connect([
    store.KEYS.IS_DOC_EMPTY,
])(AIRecommendTitle);
